import type { QuickBooksFpaLayoutNode } from '../../data/ServerApiClient'

export function normalizeQuickBooksFpaSortOrder(value: unknown, fallback = 0): number {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? Math.round(numeric) : fallback
}

export function compareQuickBooksFpaLayoutNodes(a: QuickBooksFpaLayoutNode, b: QuickBooksFpaLayoutNode): number {
  return statementSortRank(a) - statementSortRank(b)
    || Number(a.sort_order || 0) - Number(b.sort_order || 0)
    || accountTypeSortRank(a) - accountTypeSortRank(b)
    || compareAccountNumbers(a.account_number, b.account_number)
    || String(a.qbo_fully_qualified_name || a.display_name || '').localeCompare(String(b.qbo_fully_qualified_name || b.display_name || ''), undefined, { numeric: true, sensitivity: 'base' })
    || String(a.id || '').localeCompare(String(b.id || ''))
}

export function buildQuickBooksFpaInsertRowPatch(args: {
  id?: string
  targetNode: QuickBooksFpaLayoutNode | null | undefined
  nodes: QuickBooksFpaLayoutNode[]
  statementType?: string
  nodeType?: string
  displayName?: string
}): (Partial<QuickBooksFpaLayoutNode> & { id: string; clear_parent?: boolean; clear_cash_flow_section?: boolean }) | null {
  const target = args.targetNode
  if (!target) return null
  const id = String(args.id || '').trim() || createClientUuid()
  const nodeType = String(args.nodeType || 'group').trim() || 'group'
  const statement = String(target.statement_type || args.statementType || 'excluded').trim() || 'excluded'
  const parentId = String(target.parent_node_id || '').trim()
  const cashFlowSection = String(target.cash_flow_section || '').trim()
  const targetSortOrder = normalizeQuickBooksFpaSortOrder(target.sort_order)
  const siblingRows = args.nodes
    .filter((candidate) => (
      candidate.id !== id
      && String(candidate.parent_node_id || '').trim() === parentId
      && String(candidate.statement_type || '').trim() === statement
    ))
    .sort(compareQuickBooksFpaLayoutNodes)
  const targetIndex = siblingRows.findIndex((candidate) => candidate.id === target.id)
  const nextSibling = targetIndex >= 0 ? siblingRows[targetIndex + 1] : null
  const nextSortOrder = nextSibling && Number.isFinite(Number(nextSibling.sort_order))
    ? normalizeQuickBooksFpaSortOrder(nextSibling.sort_order)
    : null
  const maxSiblingSortOrder = Math.max(targetSortOrder, ...siblingRows.map((node) => (
    normalizeQuickBooksFpaSortOrder(node.sort_order)
  )))
  const rawSortOrder = nextSortOrder != null && nextSortOrder > targetSortOrder
    ? targetSortOrder + (nextSortOrder - targetSortOrder) / 2
    : maxSiblingSortOrder + 10
  const sortOrder = normalizeQuickBooksFpaSortOrder(rawSortOrder)

  return {
    id,
    node_type: nodeType,
    statement_type: statement,
    parent_node_id: parentId || undefined,
    clear_parent: !parentId,
    cash_flow_section: cashFlowSection || undefined,
    clear_cash_flow_section: !cashFlowSection,
    display_name: String(args.displayName || '').trim() || 'New row',
    include_in_reports: nodeType !== 'spacer' && target.include_in_reports !== false,
    sort_order: sortOrder,
    formula_json: nodeType === 'formula' ? { op: 'sum', node_ids: [] } : null,
    metadata: {
      cfoCoreUserInsertedRow: true,
      cfoCoreInsertedNearNodeId: target.id,
    },
  }
}

export type QuickBooksFpaDropPosition = 'above' | 'below' | 'inside'

export function buildQuickBooksFpaMoveRowPatch(args: {
  dragged: QuickBooksFpaLayoutNode
  target: QuickBooksFpaLayoutNode
  nodes: QuickBooksFpaLayoutNode[]
  position: QuickBooksFpaDropPosition
}): (Partial<QuickBooksFpaLayoutNode> & { id: string; clear_parent?: boolean; clear_cash_flow_section?: boolean }) | null {
  const { dragged, target, nodes } = args
  const nodeById = new Map(nodes.map((node) => [node.id, node]))
  const targetCanParent = target.node_type !== 'account' && target.node_type !== 'spacer'
  const inside = args.position === 'inside' && targetCanParent
  const parentId = inside ? target.id : String(target.parent_node_id || '').trim()
  const parent = parentId ? nodeById.get(parentId) : null
  const statement = String((inside ? target.statement_type : parent?.statement_type || target.statement_type) || dragged.statement_type || 'excluded').trim()
  const cashFlowSection = String((inside ? target.cash_flow_section : parent?.cash_flow_section || target.cash_flow_section) || '').trim()
  const siblings = nodes
    .filter((candidate) => (
      candidate.id !== dragged.id
      && String(candidate.parent_node_id || '').trim() === parentId
      && String(candidate.statement_type || '').trim() === statement
    ))
    .sort(compareQuickBooksFpaLayoutNodes)

  let previous: QuickBooksFpaLayoutNode | null = null
  let next: QuickBooksFpaLayoutNode | null = null
  if (inside) {
    previous = siblings[siblings.length - 1] || null
  } else {
    const targetIndex = siblings.findIndex((candidate) => candidate.id === target.id)
    if (targetIndex < 0) return null
    if (args.position === 'above') {
      previous = siblings[targetIndex - 1] || null
      next = siblings[targetIndex] || null
    } else {
      previous = siblings[targetIndex] || null
      next = siblings[targetIndex + 1] || null
    }
  }

  const previousSortOrder = Number(previous?.sort_order)
  const nextSortOrder = Number(next?.sort_order)
  const rawSortOrder = Number.isFinite(previousSortOrder) && Number.isFinite(nextSortOrder) && nextSortOrder > previousSortOrder
    ? previousSortOrder + (nextSortOrder - previousSortOrder) / 2
    : Number.isFinite(previousSortOrder)
      ? previousSortOrder + 10
      : Number.isFinite(nextSortOrder)
        ? nextSortOrder - 10
        : 10
  const sortOrder = normalizeQuickBooksFpaSortOrder(rawSortOrder)

  const patch: Partial<QuickBooksFpaLayoutNode> & { id: string; clear_parent?: boolean; clear_cash_flow_section?: boolean } = { id: dragged.id }
  if (String(dragged.parent_node_id || '').trim() !== parentId) {
    if (parentId) patch.parent_node_id = parentId
    else patch.clear_parent = true
  }
  if (Number(dragged.sort_order || 0) !== sortOrder) patch.sort_order = sortOrder
  if (String(dragged.statement_type || '').trim() !== statement) patch.statement_type = statement
  if (String(dragged.cash_flow_section || '').trim() !== cashFlowSection) {
    if (cashFlowSection) patch.cash_flow_section = cashFlowSection
    else patch.clear_cash_flow_section = true
  }
  return Object.keys(patch).length > 1 ? patch : null
}

export function formatQuickBooksFpaRowLabel(
  node: Pick<QuickBooksFpaLayoutNode, 'account_number' | 'display_name' | 'include_in_reports'>,
  formatting: { showAccountCodes?: boolean } | null | undefined,
): string {
  const hiddenPrefix = node.include_in_reports === false ? '[hidden] ' : ''
  const accountCode = String(node.account_number || '').trim()
  const accountCodePrefix = formatting?.showAccountCodes && accountCode ? `${accountCode} ` : ''
  return `${hiddenPrefix}${accountCodePrefix}${String(node.display_name || '').trim()}`
}

export type QuickBooksFpaGridTheme = {
  accent: string
  accentSoft: string
  bodyText: string
  border: string
  dragBg: string
  dropBg: string
  headerBg: string
  headerBorder: string
  headerText: string
  hiddenText: string
  multiSelectedBg: string
  percentText: string
  rowBg: string
  rowBorder: string
  selectedBg: string
  stickyBorder: string
  subtotalBorder: string
  syntheticBg: string
  toggleBg: string
  toggleBorder: string
  toggleText: string
}

export function quickBooksFpaGridTheme(theme: string | null | undefined): QuickBooksFpaGridTheme {
  if (theme === 'board') {
    return {
      accent: '#0f172a',
      accentSoft: '#e2e8f0',
      bodyText: '#111827',
      border: '#cbd5e1',
      dragBg: '#e5e7eb',
      dropBg: '#e0f2fe',
      headerBg: '#0f172a',
      headerBorder: '#0f172a',
      headerText: '#f8fafc',
      hiddenText: '#94a3b8',
      multiSelectedBg: '#e0f2fe',
      percentText: '#0369a1',
      rowBg: '#ffffff',
      rowBorder: '#e2e8f0',
      selectedBg: '#f1f5f9',
      stickyBorder: '#cbd5e1',
      subtotalBorder: '#0f172a',
      syntheticBg: '#f1f5f9',
      toggleBg: '#f8fafc',
      toggleBorder: '#94a3b8',
      toggleText: '#0f172a',
    }
  }
  if (theme === 'minimal') {
    return {
      accent: '#6b7280',
      accentSoft: '#f3f4f6',
      bodyText: '#111827',
      border: '#e5e7eb',
      dragBg: '#f9fafb',
      dropBg: '#f3f4f6',
      headerBg: '#ffffff',
      headerBorder: '#e5e7eb',
      headerText: '#111827',
      hiddenText: '#9ca3af',
      multiSelectedBg: '#f9fafb',
      percentText: '#374151',
      rowBg: '#ffffff',
      rowBorder: '#f3f4f6',
      selectedBg: '#f9fafb',
      stickyBorder: '#e5e7eb',
      subtotalBorder: '#6b7280',
      syntheticBg: '#ffffff',
      toggleBg: '#ffffff',
      toggleBorder: '#d1d5db',
      toggleText: '#374151',
    }
  }
  return {
    accent: '#138a45',
    accentSoft: '#ecfdf5',
    bodyText: '#111827',
    border: '#d1d5db',
    dragBg: '#f3f4f6',
    dropBg: '#dcfce7',
    headerBg: '#f8faf9',
    headerBorder: '#d1d5db',
    headerText: '#374151',
    hiddenText: '#9ca3af',
    multiSelectedBg: '#e8f8ef',
    percentText: '#047857',
    rowBg: '#ffffff',
    rowBorder: '#eef2f0',
    selectedBg: '#ecfdf5',
    stickyBorder: '#dbe7df',
    subtotalBorder: '#111827',
    syntheticBg: '#f7fbf8',
    toggleBg: '#ecfdf5',
    toggleBorder: '#a7f3d0',
    toggleText: '#047857',
  }
}

function createClientUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16)
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8
    return value.toString(16)
  })
}

function statementSortRank(node: QuickBooksFpaLayoutNode): number {
  if (node.statement_type === 'profit_and_loss') return 10
  if (node.statement_type === 'balance_sheet') return 20
  if (node.statement_type === 'cash_flow') return 30
  return 90
}

function accountTypeSortRank(node: QuickBooksFpaLayoutNode): number {
  const classification = String(node.qbo_classification || '').toLowerCase()
  const type = String(node.qbo_account_type || '').toLowerCase()
  if (node.statement_type === 'profit_and_loss') {
    if (classification === 'revenue' || type.includes('income')) return 10
    if (type.includes('cost') || type.includes('cogs')) return 20
    if (classification === 'expense' || type.includes('expense')) return 30
    if (type.includes('otherincome') || type.includes('other income')) return 40
    if (type.includes('otherexpense') || type.includes('other expense')) return 50
    return 60
  }
  if (node.statement_type === 'balance_sheet') {
    if (classification === 'asset' || type.includes('bank') || type.includes('asset')) return 10
    if (classification === 'liability' || type.includes('liability') || type.includes('creditcard')) return 20
    if (classification === 'equity' || type.includes('equity')) return 30
    return 60
  }
  if (node.cash_flow_section === 'cash') return 10
  if (node.cash_flow_section === 'operating') return 20
  if (node.cash_flow_section === 'investing') return 30
  if (node.cash_flow_section === 'financing') return 40
  return 60
}

function compareAccountNumbers(left?: string | null, right?: string | null): number {
  const a = String(left || '').trim()
  const b = String(right || '').trim()
  if (!a && !b) return 0
  if (!a) return 1
  if (!b) return -1
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}
